import type { EmployeeDetail, RevealEmployeeField } from "@evolua/api-client";
import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  DescriptionList,
  EmptyState,
  ErrorState,
  Field,
  FreshnessBadge,
  Inline,
  PageHeader,
  PartialState,
  Skeleton,
  Table,
  Tabs,
  Textarea,
  badgeToneForStatus,
  type DescriptionItem,
} from "@evolua/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useEmployee,
  useEmployeeCareerHistory,
  usePatchEmployee,
  useRevealEmployeeField,
} from "../api/use-employees.js";
import { useSession } from "../session/SessionContext.js";

type TabId =
  "cadastro" | "vinculo" | "historico" | "ferias" | "folha" | "beneficios" | "documentos";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "cadastro", label: "Cadastro" },
  { id: "vinculo", label: "Vínculo" },
  { id: "historico", label: "Histórico" },
  { id: "ferias", label: "Férias" },
  { id: "folha", label: "Folha" },
  { id: "beneficios", label: "Benefícios" },
  { id: "documentos", label: "Documentos" },
];

function originOf(detail: EmployeeDetail, field: string) {
  return detail.fieldOrigins.find((f) => f.field === field)?.ownership ?? "ERP";
}

function OriginBadge({ ownership }: { ownership: string }) {
  const tone =
    ownership === "PLATFORM" ? "neutral" : ownership === "HYBRID" ? "warning" : "brand";
  return <Badge tone={tone}>{ownership}</Badge>;
}

function fieldItem(
  label: string,
  ownership: string,
  description: ReactNode,
): DescriptionItem {
  return {
    term: label,
    aside: <OriginBadge ownership={ownership} />,
    description,
  };
}

export function EmployeeDetailPage({ backTo }: { backTo: "/equipe" | "/pessoas" }) {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { isDemoMode, session } = useSession();
  const [tab, setTab] = useState<TabId>("cadastro");
  const [revealed, setRevealed] = useState<Partial<Record<RevealEmployeeField, string>>>(
    {},
  );
  const [lastAudit, setLastAudit] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const query = useEmployee(employeeId);
  const careerQuery = useEmployeeCareerHistory(employeeId);
  const reveal = useRevealEmployeeField(employeeId);
  const patch = usePatchEmployee(employeeId);
  const detail = query.data;

  const canRevealSensitive = useMemo(
    () =>
      Boolean(
        session?.permissions.includes("employee:read_sensitive") ||
        session?.permissions.includes("*"),
      ),
    [session?.permissions],
  );
  const canRevealSalary = useMemo(
    () => canRevealSensitive || Boolean(session?.permissions.includes("payroll:read")),
    [canRevealSensitive, session?.permissions],
  );
  const canEdit = useMemo(
    () =>
      Boolean(
        session?.permissions.includes("employee:write") ||
        session?.permissions.includes("*"),
      ),
    [session?.permissions],
  );

  useEffect(() => {
    if (!detail || editing) return;
    setDisplayNameDraft(detail.displayName ?? "");
    setNotesDraft(detail.internalNotes ?? "");
  }, [detail, editing]);

  const correlationId =
    query.error instanceof EvoluaApiError ? query.error.problem.traceId : undefined;

  async function onReveal(field: RevealEmployeeField) {
    const result = await reveal.mutateAsync(field);
    setRevealed((prev) => ({ ...prev, [field]: String(result.value) }));
    setLastAudit(result.auditId);
  }

  async function onSavePlatform() {
    if (!detail) return;
    setEditError(null);
    try {
      const updated = await patch.mutateAsync({
        expectedVersion: detail.version,
        displayName: displayNameDraft.trim() || null,
        internalNotes: notesDraft.trim() || null,
      });
      setLastAudit(updated.fieldHistory[0]?.auditId ?? null);
      setEditing(false);
    } catch (err) {
      const message =
        err instanceof EvoluaApiError
          ? err.problem.detail
          : err instanceof Error
            ? err.message
            : "Falha ao salvar";
      setEditError(message);
    }
  }

  if (query.isPending) {
    return (
      <div className="evolua-stack evolua-stack--2" aria-busy="true">
        <Skeleton height="2rem" label="Carregando colaborador" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (query.isError || !detail) {
    return (
      <>
        <p>
          <Link to={backTo}>← Voltar</Link>
        </p>
        <ErrorState
          {...(correlationId ? { correlationId } : {})}
          onRetry={() => void query.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <p>
        <Link to={backTo}>← Voltar à listagem</Link>
      </p>

      <PageHeader
        title={detail.fullName}
        lead={`Detalhe do colaborador — ownership, edição da plataforma e revelação auditada${isDemoMode ? " (dados demo)." : "."}`}
        freshness={
          <FreshnessBadge syncedAt={detail.syncedAt ? new Date(detail.syncedAt) : null} />
        }
      />

      {isDemoMode ? (
        <PartialState description="Abas de férias/folha/benefícios/documentos usam fixtures até as APIs T33–T40 e o piloto P-001." />
      ) : null}

      {lastAudit ? (
        <PartialState
          title="Ação auditada"
          description={`Último registro · ${lastAudit}`}
        />
      ) : null}

      <Tabs items={TABS} value={tab} onChange={(id) => setTab(id as TabId)}>
        {tab === "cadastro" ? (
          <div className="evolua-stack evolua-stack--4">
            <DescriptionList
              items={[
                fieldItem(
                  "Nome completo",
                  originOf(detail, "fullName"),
                  <>
                    {detail.fullName}
                    <span className="evolua-muted">
                      Origem ERP — não editável na plataforma
                    </span>
                  </>,
                ),
                fieldItem(
                  "Apelido (plataforma)",
                  originOf(detail, "displayName"),
                  editing ? (
                    <Field
                      label="Apelido"
                      value={displayNameDraft}
                      onChange={(e) => setDisplayNameDraft(e.target.value)}
                      maxLength={120}
                      aria-label="Apelido"
                    />
                  ) : (
                    (detail.displayName ?? "—")
                  ),
                ),
                fieldItem(
                  "E-mail",
                  originOf(detail, "email"),
                  <>
                    {detail.email ?? "—"}
                    {!detail.emailEditable ? (
                      <span className="evolua-muted">
                        HÍBRIDO já preenchido pelo ERP — edição bloqueada na API
                      </span>
                    ) : null}
                  </>,
                ),
                fieldItem(
                  "CPF",
                  originOf(detail, "cpf"),
                  <>
                    {revealed.cpf ?? detail.cpf ?? "—"}
                    {canRevealSensitive && !revealed.cpf ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={reveal.isPending}
                        onClick={() => void onReveal("cpf")}
                      >
                        Revelar
                      </Button>
                    ) : null}
                  </>,
                ),
                fieldItem(
                  "Dados bancários",
                  originOf(detail, "bankAccount"),
                  <>
                    {revealed.bankAccount ?? detail.bankAccount ?? "—"}
                    {canRevealSensitive && !revealed.bankAccount ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={reveal.isPending}
                        onClick={() => void onReveal("bankAccount")}
                      >
                        Revelar
                      </Button>
                    ) : null}
                  </>,
                ),
                fieldItem(
                  "Notas internas",
                  originOf(detail, "internalNotes"),
                  editing ? (
                    <Textarea
                      label="Notas internas"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      maxLength={4000}
                      rows={3}
                      aria-label="Notas internas"
                    />
                  ) : (
                    (detail.internalNotes ?? "—")
                  ),
                ),
              ]}
            />

            {canEdit ? (
              <Inline>
                {editing ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      disabled={patch.isPending}
                      onClick={() => void onSavePlatform()}
                    >
                      Salvar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={patch.isPending}
                      onClick={() => {
                        setEditing(false);
                        setEditError(null);
                        setDisplayNameDraft(detail.displayName ?? "");
                        setNotesDraft(detail.internalNotes ?? "");
                      }}
                    >
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button type="button" size="sm" onClick={() => setEditing(true)}>
                    Editar campos da plataforma
                  </Button>
                )}
                <span className="evolua-muted">versão {detail.version}</span>
              </Inline>
            ) : null}

            {editError ? <Alert tone="danger">{editError}</Alert> : null}

            {detail.fieldHistory.length > 0 ? (
              <div className="evolua-stack evolua-stack--3">
                <h2 className="evolua-section-title">Histórico de alterações</h2>
                <Table
                  caption="Alterações de campos"
                  getRowId={(h) => h.id}
                  columns={[
                    { id: "field", header: "Campo", cell: (h) => h.field },
                    { id: "from", header: "De", cell: (h) => h.previous ?? "—" },
                    { id: "to", header: "Para", cell: (h) => h.next ?? "—" },
                    {
                      id: "at",
                      header: "Quando",
                      cell: (h) => new Date(h.at).toLocaleString("pt-BR"),
                    },
                  ]}
                  rows={detail.fieldHistory.slice(0, 8)}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "vinculo" ? (
          <DescriptionList
            items={[
              fieldItem(
                "Status",
                originOf(detail, "status"),
                <Badge tone={badgeToneForStatus(detail.status)}>{detail.status}</Badge>,
              ),
              fieldItem(
                "Matrícula",
                originOf(detail, "registrationNumber"),
                detail.registrationNumber ?? "—",
              ),
              fieldItem(
                "Área",
                originOf(detail, "departmentName"),
                detail.departmentName,
              ),
              fieldItem(
                "Cargo",
                originOf(detail, "positionName"),
                detail.positionName ?? "—",
              ),
              fieldItem("Admissão", originOf(detail, "hireDate"), detail.hireDate ?? "—"),
              fieldItem(
                "Salário",
                originOf(detail, "salaryAmount"),
                <>
                  {revealed.salaryAmount
                    ? Number(revealed.salaryAmount).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })
                    : "••••"}
                  {canRevealSalary && !revealed.salaryAmount ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={reveal.isPending}
                      onClick={() => void onReveal("salaryAmount")}
                    >
                      Revelar
                    </Button>
                  ) : null}
                </>,
              ),
            ]}
          />
        ) : null}

        {tab === "historico" ? (
          careerQuery.isLoading ? (
            <Skeleton height={80} />
          ) : careerQuery.isError ? (
            <ErrorState title="Falha ao carregar histórico" />
          ) : !careerQuery.data?.data.length ? (
            <EmptyState
              title="Sem histórico"
              description="Nenhuma mudança detectada na sincronização."
            />
          ) : (
            <Table
              caption="Histórico de carreira"
              getRowId={(e) => e.id}
              columns={[
                { id: "type", header: "Tipo", cell: (e) => e.changeType },
                {
                  id: "when",
                  header: "Data",
                  cell: (e) => new Date(e.occurredAt).toLocaleDateString("pt-BR"),
                },
                {
                  id: "change",
                  header: "Alteração",
                  cell: (e) => (
                    <>
                      {e.fromLabel ?? "—"} → {e.toLabel ?? "—"}
                      {e.salaryRedacted ? (
                        <Badge tone="warning">salário oculto</Badge>
                      ) : e.salaryAmount != null ? (
                        <span>
                          {" "}
                          (
                          {e.salaryAmount.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                          )
                        </span>
                      ) : null}
                    </>
                  ),
                },
                { id: "source", header: "Origem", cell: (e) => e.source },
              ]}
              rows={careerQuery.data.data}
            />
          )
        ) : null}

        {tab === "ferias" ? (
          <Table
            caption="Períodos de férias"
            getRowId={(p) => p.label}
            emptyState={
              <EmptyState
                title="Sem períodos"
                description="Nenhum saldo de férias disponível."
              />
            }
            columns={[
              { id: "label", header: "Período", cell: (p) => p.label },
              {
                id: "days",
                header: "Saldo",
                align: "numeric",
                cell: (p) => `${p.balanceDays} dias`,
              },
              { id: "exp", header: "Expira", cell: (p) => p.expiresAt ?? "—" },
            ]}
            rows={detail.vacationPeriods}
          />
        ) : null}

        {tab === "folha" ? (
          <Table
            caption="Holerites"
            getRowId={(p) => p.competence}
            emptyState={
              <EmptyState
                title="Sem holerites"
                description="Nenhum holerite disponível."
              />
            }
            columns={[
              { id: "comp", header: "Competência", cell: (p) => p.competence },
              {
                id: "net",
                header: "Líquido",
                align: "numeric",
                cell: (p) =>
                  p.netAmount.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }),
              },
            ]}
            rows={detail.payslips}
          />
        ) : null}

        {tab === "beneficios" ? (
          <Table
            caption="Benefícios"
            getRowId={(b) => b.name}
            emptyState={
              <EmptyState
                title="Sem benefícios"
                description="Nenhum benefício cadastrado."
              />
            }
            columns={[
              { id: "name", header: "Benefício", cell: (b) => b.name },
              {
                id: "status",
                header: "Status",
                cell: (b) => (
                  <Badge tone={badgeToneForStatus(b.status)}>{b.status}</Badge>
                ),
              },
            ]}
            rows={detail.benefits}
          />
        ) : null}

        {tab === "documentos" ? (
          <Table
            caption="Documentos"
            getRowId={(d) => d.name}
            emptyState={
              <EmptyState
                title="Sem documentos"
                description="Nenhum documento disponível."
              />
            }
            columns={[
              { id: "name", header: "Documento", cell: (d) => d.name },
              { id: "cat", header: "Categoria", cell: (d) => d.category },
              { id: "issued", header: "Emissão", cell: (d) => d.issuedAt ?? "—" },
            ]}
            rows={detail.documents}
          />
        ) : null}
      </Tabs>
    </>
  );
}
